class GlowSwitch {
  constructor() {
    this.isGlowMode = false;
    this.wasNightMode = false;
    this.currentVariantId = null;
    this.originalButtonColor = null;
    this.originalButtonTextColor = null;
    this.originalButtonHoverColor = null;
    this.originalButtonTextHoverColor = null;
    this.nightModeEnabledOnLoad = null;
    this.contrastModeEnabledOnLoad = null;
    
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  init() {
    // Add a small delay to ensure Shopify's variant scripts are loaded
    this.nightModeEnabledOnLoad = localStorage.getItem('nightModeEnabled');
    this.contrastModeEnabledOnLoad = localStorage.getItem('contrastModeEnabled');
    setTimeout(() => {
      if (this.setupSwitch()) {
        this.setupVariantChangeListener();
        this.setupThumbnailClickHandlers();
        this.setupGlowElements();
      }
    }, 100);
  }

  setupGlowElements() {
    // Get all product images
    const productImages = document.querySelectorAll('.product__photo');
    if (!productImages.length) {
      console.warn('No product images found');
      return;
    }

    // For each image, create a glow version if it doesn't exist
    productImages.forEach(image => {
      if (!image.dataset.glowSrc) {
        const regularSrc = image.src;
        // Assuming glow images follow a naming convention with '-GID-glowing' suffix
        const glowSrc = regularSrc.replace(/(\.[^.]+)$/, '-GID-glowing$1');
        image.dataset.glowSrc = glowSrc;
        image.dataset.regularSrc = regularSrc;
      }
    });

    // Store original button CSS variables
    const root = document.documentElement;
    const computedStyle = getComputedStyle(root);
    
    // Get the actual color values
    this.originalButtonColor = computedStyle.getPropertyValue('--color_button').trim();
    this.originalButtonTextColor = computedStyle.getPropertyValue('--color_button_text').trim();
    this.originalButtonHoverColor = computedStyle.getPropertyValue('--color_button_hover').trim();
    this.originalButtonTextHoverColor = computedStyle.getPropertyValue('--color_button_text_hover').trim();
  }

  setupSwitch() {
    const switchHTML = `
      <div id="glow-switch" class="glow-switch-container" role="switch" aria-checked="false" tabindex="0">
        <div class="toggle-switch"></div>
        <span class="glow-switch-text">Glow Preview</span>
        <div class="glow-tooltip">
          <div class="glow-tooltip-text">Toggle glow mode to preview how your keyboard will look in the dark</div>
          <button class="glow-tooltip-dismiss">DISMISS</button>
        </div>
      </div>
    `;

    const photosGrid = document.querySelector('.grid__item.product-single__photos');
    if (!photosGrid) return false;

    // Make sure container has relative positioning for absolute toggle
    photosGrid.style.position = 'relative';
    photosGrid.insertAdjacentHTML('afterbegin', switchHTML);

    // Setup click handler
    const glowSwitch = document.getElementById('glow-switch');
    if (!glowSwitch) return false;

    glowSwitch.addEventListener('click', () => this.toggleGlowMode());
    glowSwitch.addEventListener('keypress', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.toggleGlowMode();
      }
    });

    // Setup tooltip
    this.setupTooltip();

    return true;
  }

  setupVariantChangeListener() {
    // Listen for changes on the variant selectors
    const selects = document.getElementsByClassName('single-option-selector');
    for (let select of selects) {
      select.addEventListener('change', () => {
        // First fade out the featured image
        const featuredImage = document.querySelector('.product-single__photo:not(.hide)');
        if (featuredImage) {
          featuredImage.style.opacity = '0';
        }
        
        // Add a small delay to let other handlers complete first
        setTimeout(() => {

          
          // Find the current thumbnail
          const currentImage = document.querySelector('.product-single__photo:not(.hide)');
          if (!currentImage) return;

          const currentThumbnail = document.querySelector(`.product-single__thumbnail[data-thumbnail-id="${currentImage.dataset.mediaId}"]`);
          if (!currentThumbnail) return;

          const currentAltText = currentThumbnail.querySelector('img')?.alt || '';


          // If we're in glow mode but showing a non-glowing image, switch to glowing
          if (this.isGlowMode && currentThumbnail.classList.contains('not-glow-mode-image')) {

            const glowingThumbnail = this.findMatchingThumbnail(currentAltText, true);
            if (glowingThumbnail) {
              glowingThumbnail.click();
              // Fade in the new image
              setTimeout(() => {
                const newImage = document.querySelector('.product-single__photo:not(.hide)');
                if (newImage) {
                  newImage.style.opacity = '1';
                }
              }, 50);
            }
          }
          // If we're in normal mode but showing a glowing image, switch to non-glowing
          else if (!this.isGlowMode && currentThumbnail.classList.contains('glow-mode-image')) {
            const notGlowingThumbnail = this.findMatchingThumbnail(currentAltText, false);
            if (notGlowingThumbnail) {
              notGlowingThumbnail.click();
              // Fade in the new image
              setTimeout(() => {
                const newImage = document.querySelector('.product-single__photo:not(.hide)');
                if (newImage) {
                  newImage.style.opacity = '1';
                }
              }, 50);
            }
          } else {
            // If we don't need to switch versions, just fade in the current image
            setTimeout(() => {
              if (currentImage) {
                currentImage.style.opacity = '1';
              }
            }, 50);
          }
        }, 100); // 100ms delay should be enough for other handlers
      });
    }
  }

  setupThumbnailClickHandlers() {
    // Get both desktop and mobile thumbnails
    const thumbnails = document.querySelectorAll('.product-single__thumbnail');
    thumbnails.forEach(thumbnail => {
      thumbnail.addEventListener('click', (e) => {
        e.preventDefault(); // Prevent the default anchor tag behavior
        
        // First fade out the current image
        const currentImage = document.querySelector('.product-single__photo:not(.hide)');
        if (currentImage) {
          currentImage.style.opacity = '0';
        }

        // Let Shopify's thumbnail click handler run
        setTimeout(() => {
          // After a short delay, fade in the new image
          const newImage = document.querySelector('.product-single__photo:not(.hide)');
          if (newImage) {
            newImage.style.opacity = '1';
          }
          // Update the title glow if in glow mode
          if (this.isGlowMode) {
            this.updateTitleGlow();
          }
        }, 100);
      });
    });
  }

  setupTooltip() {
    // Check if tooltip has been dismissed before
    const tooltipDismissed = localStorage.getItem('glowTooltipDismissed');
    if (tooltipDismissed === 'true') return;

    // Show tooltip
    const tooltip = document.querySelector('.glow-tooltip');
    if (!tooltip) return;

    // Add a small delay before showing the tooltip
    setTimeout(() => {
      tooltip.classList.add('show');
    }, 500);

    // Setup dismiss button
    const dismissButton = tooltip.querySelector('.glow-tooltip-dismiss');
    if (dismissButton) {
      dismissButton.addEventListener('click', (e) => {
        e.stopPropagation(); // Prevent triggering the toggle
        tooltip.classList.remove('show');
        localStorage.setItem('glowTooltipDismissed', 'true');
      });
    }
  }

  findMatchingThumbnail(currentAltText, wantGlowing) {
    // Get all thumbnails
    const allThumbnails = document.querySelectorAll('.product-single__thumbnail');
    
    // Split current alt text into words
    const currentWords = currentAltText.split(' ');
    
    // Find index of 'not' if it exists
    const notIndex = currentWords.indexOf('not');
    
    // Create the alt text we're looking for by adding or removing 'not'
    let targetWords = [...currentWords];
    if (wantGlowing && notIndex !== -1) {
      // Remove 'not' to get glowing version
      targetWords.splice(notIndex, 1);
    } else if (!wantGlowing && notIndex === -1) {
      // Add 'not' before 'glowing' to get non-glowing version
      const glowingIndex = targetWords.indexOf('glowing');
      if (glowingIndex !== -1) {
        targetWords.splice(glowingIndex, 0, 'not');
      }
    }
    
    const targetAltText = targetWords.join(' ');
    
    // Find thumbnail with matching alt text
    return Array.from(allThumbnails).find(thumb => {
      const alt = thumb.querySelector('img')?.alt || '';
      return alt === targetAltText;
    });
  }

  updateVariantImages() {
    const productImages = document.querySelectorAll('.product__photo');
    productImages.forEach(image => {
      if (this.isGlowMode) {
        // Switch to glow version
        const glowSrc = image.dataset.glowSrc;
        if (glowSrc) {
          image.src = glowSrc;
        }
      } else {
        // Switch to regular version
        const regularSrc = image.dataset.regularSrc;
        if (regularSrc) {
          image.src = regularSrc;
        }
      }
    });
    // Update glow effects for new variant
    if (this.isGlowMode) {
      this.enableGlowMode();
      this.updateNavGradient();
    }
  }

  updateNavGradient() {
    const navGradients = document.querySelectorAll('.nav-gradient');
    const root = document.documentElement;
    const glowColor = getComputedStyle(root).getPropertyValue('--glow-color').trim();
    
    navGradients.forEach(gradient => {
      if (this.isGlowMode) {
        gradient.style.backgroundImage = `linear-gradient(to top, rgba(26, 37, 28, 0) 90%, ${glowColor}29)`;
      } else {
        gradient.style.backgroundImage = 'linear-gradient(to top, rgba(26, 37, 28, 0) 90%, rgba(200, 200, 200, 0.29))';
      }

    });
  }

  updateProductFlagGlow() {
    const productFlags = document.querySelectorAll('.product-flag');
    productFlags.forEach(flag => {
      if (this.isGlowMode) {
        const computedStyle = getComputedStyle(flag);
        const borderColor = computedStyle.borderColor;
        flag.style.boxShadow = `0 0 30px ${borderColor}`;
      } else {
        flag.style.boxShadow = 'none';
      }
    });
  }

  toggleGlowMode() {
    this.isGlowMode = !this.isGlowMode;
    
    // Update switch text and aria state
    const glowSwitch = document.getElementById('glow-switch');
    if (glowSwitch) {
      glowSwitch.classList.toggle('glow-mode-active', this.isGlowMode);
      glowSwitch.setAttribute('aria-checked', this.isGlowMode);
      const switchText = glowSwitch.querySelector('.glow-switch-text');
      if (switchText) {
        switchText.textContent = 'Glow Preview';
      }
    }

    // Update product flag glows
    this.updateProductFlagGlow();

    // Update CSS variables for glow color
    const colorSelect = document.querySelector('.single-option-selector');
    if (!colorSelect) return;
    
    const currentColor = colorSelect.value;
    
    // Get all glow color blocks from the theme settings
    const glowBlocks = Array.from(document.querySelectorAll('[data-glow-block]'));
    const matchingBlock = glowBlocks.find(block => {
      const colorName = block.getAttribute('data-glow-color-name');
      return colorName && colorName.toLowerCase() === currentColor.toLowerCase();
    });

    let glowColor = matchingBlock ? matchingBlock.getAttribute('data-glow-color') : '#FFF';
    
    // Set CSS variables
    const root = document.documentElement;
    
    // Always update glow color variables
    root.style.setProperty('--glow-color', glowColor);
    root.style.setProperty('--glow-color-alpha', glowColor + '88');
    root.style.setProperty('--glow-color-alpha2', glowColor + '44');
    root.style.setProperty('--glow-color-alpha3', glowColor + '22');

    if (this.isGlowMode) {
      if(this.nightModeEnabledOnLoad === 'false' || this.nightModeEnabledOnLoad === null) {
        enableNightMode();
        localStorage.setItem('nightModeEnabled', 'false');
      }
    }
    else {
      if(this.nightModeEnabledOnLoad === 'false' || this.nightModeEnabledOnLoad === null) {
        disableNightMode();
        localStorage.setItem('nightModeEnabled', 'false');
      }
      // Reset nav gradient when turning off glow mode
      const navGradients = document.querySelectorAll('.nav-gradient');
      navGradients.forEach(gradient => {
        gradient.style.backgroundImage = 'linear-gradient(to top, rgba(26, 37, 28, 0) 90%, rgba(200, 200, 200, 0.29))';
      });
    }

    // Update nav gradient
    this.updateNavGradient();

    if (this.isGlowMode) {
      // Set button colors to glow color
      root.style.setProperty('--color_button', glowColor);
      root.style.setProperty('--color_button_text', '#FFFFFF');
      root.style.setProperty('--color_button_hover', glowColor);
      root.style.setProperty('--color_button_text_hover', '#FFFFFF');
    } else {
      // Restore original button colors
      root.style.removeProperty('--color_button');
      root.style.removeProperty('--color_button_text');
      root.style.removeProperty('--color_button_hover');
      root.style.removeProperty('--color_button_text_hover');
    }

    // Add glow mode active class to body for global styles
    document.body.classList.toggle('glow-mode-active', this.isGlowMode);
    
    // First fade out the current image
    const currentImage = document.querySelector('.product-single__photo:not(.hide)');
    if (currentImage) {
      currentImage.style.opacity = '0';
    }

    // Toggle visibility of thumbnails for both desktop and mobile
    const glowContainers = document.querySelectorAll('.glow-mode-container');
    const notGlowContainers = document.querySelectorAll('.not-glow-mode-container');
    const glowThumbnails = document.querySelectorAll('.glow-mode-image');
    const notGlowThumbnails = document.querySelectorAll('.not-glow-mode-image');
    const regularThumbnails = document.querySelectorAll('.product-single__thumbnail:not(.glow-mode-image):not(.not-glow-mode-image)');
    
    // Handle mobile thumbnails (with containers)
    glowContainers.forEach(container => {
      container.style.display = this.isGlowMode ? 'block' : 'none';
      const thumbnail = container.querySelector('.glow-mode-image');
      if (thumbnail) {
        thumbnail.parentElement.style.display = this.isGlowMode ? 'block' : 'none';
      }
    });
    
    notGlowContainers.forEach(container => {
      container.style.display = this.isGlowMode ? 'none' : 'block';
      const thumbnail = container.querySelector('.not-glow-mode-image');
      if (thumbnail) {
        thumbnail.parentElement.style.display = this.isGlowMode ? 'none' : 'block';
      }
    });
    
    // Handle desktop thumbnails (direct anchors)
    glowThumbnails.forEach(thumb => {
      if (!thumb.closest('.product__photo-thumbs')) { // Only target desktop thumbnails
        thumb.style.display = this.isGlowMode ? 'block' : 'none';
      }
    });
    
    notGlowThumbnails.forEach(thumb => {
      if (!thumb.closest('.product__photo-thumbs')) { // Only target desktop thumbnails
        thumb.style.display = this.isGlowMode ? 'none' : 'block';
      }
    });
    
    // Regular thumbnails (without glowing/not glowing) stay visible
    regularThumbnails.forEach(thumb => {
      const container = thumb.closest('.product__photo-thumbs');
      if (container) {
        container.style.display = 'block';
        thumb.style.display = 'block';
      } else {
        thumb.style.display = 'block';
      }
    });

    // Add a small delay to let the fade out complete
    setTimeout(() => {
      const currentThumbnail = document.querySelector(`.product-single__thumbnail[data-thumbnail-id="${currentImage?.dataset.mediaId}"]`);
      if (!currentThumbnail) return;

      const currentAltText = currentThumbnail.querySelector('img')?.alt || '';

      // If we just turned on glow mode and have a non-glowing image, switch to glowing
      if (this.isGlowMode && currentThumbnail.classList.contains('not-glow-mode-image')) {
        const glowingThumbnail = this.findMatchingThumbnail(currentAltText, true);
        if (glowingThumbnail) {
          glowingThumbnail.click();
        }
      }
      // If we just turned off glow mode and have a glowing image, switch to non-glowing
      else if (!this.isGlowMode && currentThumbnail.classList.contains('glow-mode-image')) {
        const notGlowingThumbnail = this.findMatchingThumbnail(currentAltText, false);
        if (notGlowingThumbnail) {
          notGlowingThumbnail.click();
        }
      } else {
        // If we don't need to switch versions, just fade in the current image
        setTimeout(() => {
          if (currentImage) {
            currentImage.style.opacity = '1';
          }
        }, 50);
      }
    }, 100);

    // Update the glow button state
    const glowButton = document.getElementById('glow-switch');
    if (glowButton) {
      glowButton.classList.toggle('glow-mode-active', this.isGlowMode);
    }

    // Update dark mode based on glow mode state
    if (this.isGlowMode) {
      this.updateTitleGlow();
    } else {
      this.removeTitleGlow();
    }

    // Restore original button colors when glow mode is off
    const addToCartBtn = document.querySelector('.product-form__cart-submit');
    if (addToCartBtn && !this.isGlowMode) {
      addToCartBtn.style.backgroundColor = addToCartBtn.dataset.originalBg;
      addToCartBtn.style.borderColor = addToCartBtn.dataset.originalBorder;
      addToCartBtn.style.color = addToCartBtn.dataset.originalColor;
    }
  }

  updateTitleGlow() {
    const title = document.querySelector('.product-single__title');
    if (!title) return;
    
    // Get the current color variant
    const colorSelect = document.querySelector('.single-option-selector');
    if (!colorSelect) return;
    
    const currentColor = colorSelect.value;
    
    // Get all glow color blocks from the theme settings
    const glowBlocks = Array.from(document.querySelectorAll('[data-glow-block]'));
    const colorMapping = glowBlocks.reduce((map, block) => {
      const colorName = block.dataset.colorName;
      const glowColor = block.dataset.glowColor;
      map[colorName] = glowColor;
      return map;
    }, {});
    
    const glowColor = colorMapping[currentColor] || Object.values(colorMapping)[0];
    
    // Apply the glow effect
    title.style.textShadow = `0 0 10px ${glowColor}88, 0 0 20px ${glowColor}44, 0 0 30px ${glowColor}22`;
    title.style.transition = 'text-shadow 0.3s ease';

    // Update announcement bar
    const announcementBar = document.querySelector('.announcement-bar__message');
    if (announcementBar && this.isGlowMode) {
      announcementBar.style.backgroundColor = glowColor;
    } else if (announcementBar) {
      announcementBar.style.backgroundColor = '';
    }

    // Update cart count background
    const cartCount = document.querySelector('.site-header__cart-count');
    if (cartCount && this.isGlowMode) {
      cartCount.style.backgroundColor = glowColor;
    } else if (cartCount) {
      cartCount.style.backgroundColor = '';
    }

    // Update product photos glow
    const productImages = document.querySelectorAll('.product-single__photo-wrapper img');
    productImages.forEach(img => {
      if (this.isGlowMode) {
        img.style.filter = `drop-shadow(0 0 15px ${glowColor}66) drop-shadow(0 0 30px ${glowColor}33)`;
      } else {
        img.style.filter = '';
      }
    });

    // Set CSS variables for the glow color
    document.documentElement.style.setProperty('--glow-color', glowColor);
    document.documentElement.style.setProperty('--glow-color-alpha', `${glowColor}88`);
    document.documentElement.style.setProperty('--glow-color-alpha2', `${glowColor}44`);
    document.documentElement.style.setProperty('--glow-color-alpha3', `${glowColor}22`);

    // Update nav gradient with new glow color
    const navGradients = document.querySelectorAll('.nav-gradient');
    navGradients.forEach(gradient => {
      if (this.isGlowMode) {
        gradient.style.backgroundImage = `linear-gradient(to top, rgba(26, 37, 28, 0) 90%, ${glowColor}29)`;
      }
    });

    // Update quantity input glow
    const quantityInputs = document.querySelectorAll('.product-form__item--quantity input');
    quantityInputs.forEach(input => {
      if (this.isGlowMode) {
        input.style.borderColor = glowColor;
        input.style.boxShadow = `0 0 10px ${glowColor}88, 0 0 20px ${glowColor}44`;
      } else {
        input.style.borderColor = '';
        input.style.boxShadow = '';
      }
    });

    // Add glow mode active class to body for global styles
    document.body.classList.toggle('glow-mode-active', this.isGlowMode);

    // Update product flag glows after variant change
    this.updateProductFlagGlow();
  }

  removeTitleGlow() {
    const title = document.querySelector('.product-single__title');
    if (title) {
      title.style.textShadow = '';
    }

    // Clean up announcement bar
    const announcementBar = document.querySelector('.announcement-bar__message');
    if (announcementBar) {
      announcementBar.style.backgroundColor = '';
    }

    // Clean up cart count
    const cartCount = document.querySelector('.site-header__cart-count');
    if (cartCount) {
      cartCount.style.backgroundColor = '';
    }

    // Clean up product images
    const productImages = document.querySelectorAll('.product-single__photo-wrapper img');
    productImages.forEach(img => {
      img.style.filter = '';
    });

    // Clean up quantity inputs
    const quantityInputs = document.querySelectorAll('.product-form__item--quantity input');
    quantityInputs.forEach(input => {
      input.style.borderColor = '';
      input.style.boxShadow = '';
    });

    // Remove glow mode active class
    document.body.classList.remove('glow-mode-active');
  }

  updateFeaturedImage() {
    const currentImage = document.querySelector('.product-single__photo:not(.hide)');
    if (!currentImage) return;

    const currentImageId = currentImage.dataset.mediaId;
    if (!currentImageId) return;

    // Find the corresponding thumbnail
    const currentThumbnail = document.querySelector(`.product-single__thumbnail[data-thumbnail-id="${currentImageId}"]`);
    if (!currentThumbnail) return;

    const currentAltText = currentThumbnail.querySelector('img')?.alt || '';

    // Check if it's a not-glowing image
    if (currentThumbnail.classList.contains('not-glow-mode-image') && this.isGlowMode) {
      // Find the corresponding glowing version
      const glowingThumbnail = this.findMatchingThumbnail(currentAltText, true);
      if (glowingThumbnail) {
        glowingThumbnail.click();
      }
    }
    // Check if it's a glowing image
    else if (currentThumbnail.classList.contains('glow-mode-image') && !this.isGlowMode) {
      // Find the corresponding not-glowing version
      const notGlowingThumbnail = this.findMatchingThumbnail(currentAltText, false);
      if (notGlowingThumbnail) {
        notGlowingThumbnail.click();
      }
    }
  }

  enableGlowMode() {
    this.isGlowMode = true;
    this.updateVariantImages();
  }

  enableGlowDarkMode() {
    const root = document.documentElement;
    const lightLogo = document.querySelector('.light-logo');
    const darkLogo = document.querySelector('.dark-logo');

    // Add dark mode class to body
    document.body.classList.add('glow-dark-mode');

    if (lightLogo && darkLogo && darkLogo.getAttribute('src') !== '') {
      lightLogo.style.display = 'none';
      darkLogo.style.display = 'block';
    }

    // Set all the CSS variables to match the dark theme
    root.style.setProperty('--color_nav', '#1A251C');
    root.style.setProperty('--color_nav-bar', '#1A251C');
    root.style.setProperty('--color_text', '#FFFFFF');
    root.style.setProperty('--color_bg', '#1A251C');
    root.style.setProperty('--color_footer_bg', '#1A251C');
    root.style.setProperty('--color_footer_link', '#FFFFFF');
    root.style.setProperty('--color_button', '#FFFFFF');
    root.style.setProperty('--color_button_text', '#1A251C');
    root.style.setProperty('--color_button_secondary', '#1A251C');
    root.style.setProperty('--color_button_secondary_text', '#FFFFFF');
    root.style.setProperty('--color_text_selection', '#FFFFFF');
    root.style.setProperty('--color_franchise', '#FFFFFF');
    root.style.setProperty('--color_form', '#1A251C');
    root.style.setProperty('--color_form_border', '#FFFFFF');
    root.style.setProperty('--color_nav_icon', '#FFFFFF');

    // Update nav gradients
    const navGradients = document.querySelectorAll('.nav-gradient');
    for (let i = 0; i < navGradients.length; i++) {
      navGradients[i].style.backgroundImage = 'linear-gradient(to top, rgba(26, 37, 28, 0) 90%, rgba(200, 200, 200, 0.1))';
    }
  }

  disableGlowDarkMode() {
    const root = document.documentElement;
    const lightLogo = document.querySelector('.light-logo');
    const darkLogo = document.querySelector('.dark-logo');

    // Remove dark mode class from body
    document.body.classList.remove('glow-dark-mode');

    if (lightLogo && darkLogo && darkLogo.getAttribute('src') !== '') {
      lightLogo.style.display = 'block';
      darkLogo.style.display = 'none';
    }

    // Reset all CSS variables to their light theme values
    root.style.setProperty('--color_nav', '#FFFFFF');
    root.style.setProperty('--color_nav-bar', '#FFFFFF');
    root.style.setProperty('--color_text', '#1A251C');
    root.style.setProperty('--color_bg', '#FFFFFF');
    root.style.setProperty('--color_footer_bg', '#FFFFFF');
    root.style.setProperty('--color_footer_link', '#1A251C');
    root.style.setProperty('--color_button', '#1A251C');
    root.style.setProperty('--color_button_text', '#FFFFFF');
    root.style.setProperty('--color_button_secondary', '#FFFFFF');
    root.style.setProperty('--color_button_secondary_text', '#1A251C');
    root.style.setProperty('--color_text_selection', '#1A251C');
    root.style.setProperty('--color_franchise', '#1A251C');
    root.style.setProperty('--color_form', '#FFFFFF');
    root.style.setProperty('--color_form_border', '#1A251C');
    root.style.setProperty('--color_nav_icon', '#1A251C');

    // Reset nav gradients
    const navGradients = document.querySelectorAll('.nav-gradient');
    navGradients.forEach(gradient => {
      gradient.style.backgroundImage = '';
    });
  }
}

// Initialize the glow switch
new GlowSwitch();
